import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState, type DragEvent as ReactDragEvent, type ReactNode } from "react";
import {
  Check,
  Copy,
  Pencil,
  Plane,
  Plus,
  RotateCcw,
  Trash2,
  UserRound,
  ArrowRight,
  Users,
} from "lucide-react";
import { useSectionOrder } from "@/lib/layout-order";

const SECTION_KEYS = [
  "today",
  "matrix",
  "commitments",
  "commercial",
  "upcoming",
  "all-open",
  "done",
];

const SECTION_LABELS: Record<string, string> = {
  today: "Today",
  matrix: "Matrix",
  commitments: "Regular commitments",
  commercial: "Commercial attention",
  upcoming: "Upcoming",
  "all-open": "All open",
  done: "Recently done",
};

import { AppShell } from "@/components/AppShell";
import { StartHereCard } from "@/components/onboarding/StartHereCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PlanChip } from "@/components/GoalPicker";
import {
  QUADRANTS,
  isRecurring,
  isTaskOpen,
  landsThisWeek,
  quadrantOf,
  taskWeeklyTotal,
  weekLoadHours,
} from "@/lib/hub";
import { lensSummary } from "@/lib/lens";
import { round, type Task } from "@/components/work/types";

import { useWorkHub } from "@/components/work/useWorkHub";
import {
  CollapsibleSection,
  CommitmentGroups,
  DueBadge,
  FilterChip,
  ScheduleBadge,
  TaskRow,
  TodayList,
  LensPresets,
} from "@/components/work/parts";
import { TagFilters } from "@/components/work/TagFilters";
import { AllocateDialog } from "@/components/work/AllocateDialog";
import { CapacityPanel } from "@/components/work/CapacityPanel";
import { FollowUpDialog, TaskDialog } from "@/components/work/TaskDialog";
import { CompleteDialog } from "@/components/work/CompleteDialog";
import { CommercialPrompts, useCommercialPrompts } from "@/components/crm/CommercialPrompts";

export const Route = createFileRoute("/_authenticated/work/")({
  head: () => ({
    meta: [
      { title: "Work hub — MyPriorities" },
      {
        name: "description",
        content:
          "Capture tasks with importance, urgency, support and hours, and see your Eisenhower matrix and remaining capacity.",
      },
      { property: "og:title", content: "Work hub — MyPriorities" },
      {
        property: "og:description",
        content: "Your Eisenhower matrix and weekly capacity, built from your to-do list.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: WorkHubPage,
});

function WorkHubPage() {
  const hub = useWorkHub();
  const {
    plan,
    allOpenTasks,
    oneOffTasks,
    recurringTasks,
    upcomingTasks,
    doneRecurring,
    todayTasks,
    totals,
    capacity,
    filter,
    setFilter,
    openTasks,
    lens,
    lenses,
    selectLens,
    savePreset,
    deletePreset,
  } = hub;

  const pillarBreakdown = useMemo(() => {
    const weekTasks = allOpenTasks.filter(landsThisWeek);
    const rows = plan.pillars.map((p) => {
      const owned = weekTasks.filter((t) => t.pillar_id === p.id);
      return {
        id: p.id,
        name: p.name,
        hours: owned.reduce((s, t) => s + weekLoadHours(t), 0),
        goals: plan.goals
          .filter((g) => g.pillar_id === p.id)
          .map((g) => ({
            id: g.id,
            name: g.name,
            hours: owned
              .filter((t) => t.goal_id === g.id)
              .reduce((s, t) => s + weekLoadHours(t), 0),
          }))
          .filter((g) => g.hours > 0),
      };
    });
    const unassigned = weekTasks
      .filter((t) => !t.pillar_id)
      .reduce((s, t) => s + weekLoadHours(t), 0);
    if (unassigned > 0) {
      rows.push({ id: "unassigned", name: "Unassigned", hours: unassigned, goals: [] });
    }
    return rows.filter((r) => r.hours > 0);
  }, [allOpenTasks, plan.pillars, plan.goals]);

  const activityBreakdown = useMemo(() => {
    const totals = new Map<string, number>();
    for (const t of allOpenTasks.filter(landsThisWeek)) {
      const hours = weekLoadHours(t);
      if (hours <= 0) continue;
      const label = (t.task_type || "").trim() || "Other";
      totals.set(label, (totals.get(label) ?? 0) + hours);
      const travel = Number(t.travel_hours || 0);
      if (travel > 0 && label !== "Travel") {
        totals.set(label, (totals.get(label) ?? 0) - Math.min(travel, hours));
        totals.set("Travel", (totals.get("Travel") ?? 0) + Math.min(travel, hours));
      }
    }
    return [...totals.entries()]
      .map(([name, hours]) => ({ name, hours }))
      .filter((r) => r.hours > 0.01)
      .sort((a, b) => b.hours - a.hours);
  }, [allOpenTasks]);

  const matrixTasks = oneOffTasks;
  const hiddenByWeek =
    lens.mode !== "all" ? openTasks.length - oneOffTasks.length - recurringTasks.length : 0;

  const layout = useSectionOrder("work", SECTION_KEYS);
  const commercial = useCommercialPrompts();
  const [dragKey, setDragKey] = useState<string | null>(null);
  const [allocating, setAllocating] = useState<Task | null>(null);

  const sections: Record<string, ReactNode> = {
    today: todayTasks.length ? (
      <CollapsibleSection
        storageKey="today"
        title="Today"
        description="Daily commitments and anything landing or due today."
        summary={`${todayTasks.length} item${todayTasks.length === 1 ? "" : "s"} · ${round(totals.today)}h today`}
        {...moveProps("today")}
      >
        <TodayList
          tasks={todayTasks}
          pillarName={plan.pillarName}
          goalName={plan.goalName}
          childCount={hub.childCount}
          onDone={(t) => hub.startComplete(t)}
          onEdit={hub.openEdit}
          onDuplicate={hub.openDuplicate}
          onDelete={(t) => hub.deleteTask.mutate(t.id)}
          onAllocate={setAllocating}
        />
      </CollapsibleSection>
    ) : null,

    commercial: commercial.length ? (
      <CollapsibleSection
        storageKey="commercial"
        title="Commercial attention"
        description="Renewals, stalled deals and business reviews that need a decision."
        summary={`${commercial.length} prompt${commercial.length === 1 ? "" : "s"}`}
        defaultOpen={false}
        {...moveProps("commercial")}
      >
        <CommercialPrompts />
      </CollapsibleSection>
    ) : null,

    matrix: (
      <CollapsibleSection
        storageKey="matrix"
        title="Eisenhower matrix"
        description="One-off work only — regular commitments live below."
        summary={`${matrixTasks.length} one-off task${matrixTasks.length === 1 ? "" : "s"} starting this week · ${round(totals.flexible)}h`}
        {...moveProps("matrix")}
      >
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          {QUADRANTS.map((q) => {
            const items = matrixTasks.filter((t) => quadrantOf(t.important, t.urgent) === q.key);
            return (
              <div key={q.key} className="panel p-5">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-base font-semibold">{q.title}</h3>
                  <span className="text-xs text-muted-foreground">{q.sub}</span>
                </div>
                <div className="mt-4 space-y-2">
                  {items.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Nothing here.</p>
                  ) : (
                    items.map((t: Task) => (
                      <TaskRow
                        key={t.id}
                        task={t}
                        pillarName={plan.pillarName(t.pillar_id)}
                        goalName={plan.goalName(t.goal_id)}
                        childCount={hub.childCount(t)}
                        onDone={() => hub.startComplete(t)}
                        onEdit={() => hub.openEdit(t)}
                        onDuplicate={() => hub.openDuplicate(t)}
                        onDelete={() => hub.deleteTask.mutate(t.id)}
                        onAllocate={() => setAllocating(t)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CollapsibleSection>
    ),

    commitments:
      recurringTasks.length || doneRecurring.length ? (
        <CollapsibleSection
          storageKey="commitments"
          title="Regular commitments"
          description={`Standing meetings and routines — ${round(totals.committed)}h landing this week, kept out of the matrix.`}
          summary={`${recurringTasks.length} open · ${round(totals.committed)}h this week · ${recurringTasks.filter(landsThisWeek).length} starts this week, ${recurringTasks.filter((t) => !landsThisWeek(t)).length} starts later`}
          action={
            <Button variant="outline" size="sm" asChild>
              <Link to="/work/regular-meetings">
                Regular Meetings <ArrowRight className="size-4" />
              </Link>
            </Button>
          }
          {...moveProps("commitments")}
        >
          <CommitmentGroups
            recurringTasks={recurringTasks}
            doneRecurring={doneRecurring}
            pillarName={plan.pillarName}
            goalName={plan.goalName}
            childCount={hub.childCount}
            onDone={(t) => hub.startComplete(t)}
            onEdit={hub.openEdit}
            onDuplicate={hub.openDuplicate}
            onDelete={(t) => hub.deleteTask.mutate(t.id)}
            onAllocate={setAllocating}
          />
        </CollapsibleSection>
      ) : null,

    upcoming: upcomingTasks.length ? (
      <CollapsibleSection
        storageKey="upcoming"
        title="Upcoming — not started yet"
        description="Scheduled to begin later, so they stay out of the matrix and this week's capacity."
        summary={`${upcomingTasks.length} waiting to start`}
        {...moveProps("upcoming")}
      >
        <div className="panel mt-3 divide-y divide-border">
          {upcomingTasks.map((t: Task) => (
            <div key={t.id} className="flex flex-wrap items-center gap-3 p-4">
              <span className="font-medium">{t.title}</span>
              <PlanChip
                pillarName={plan.pillarName(t.pillar_id)}
                goalName={plan.goalName(t.goal_id)}
              />
              <Badge variant="secondary">{t.task_type}</Badge>
              <ScheduleBadge task={t} />
              <span className="ml-auto text-xs text-muted-foreground">
                {taskWeeklyTotal(t)}h per occurrence
              </span>
              <Button variant="ghost" size="icon" onClick={() => hub.openEdit(t)} aria-label="Edit">
                <Pencil className="size-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => hub.deleteTask.mutate(t.id)}>
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      </CollapsibleSection>
    ) : null,

    "all-open": (
      <CollapsibleSection
        storageKey="all-open"
        title="All open one-off tasks"
        summary={`${oneOffTasks.length} task${oneOffTasks.length === 1 ? "" : "s"}`}
        {...moveProps("all-open")}
      >
        <div className="panel mt-3 divide-y divide-border">
          {oneOffTasks.length === 0 ? (
            <p className="p-5 text-sm text-muted-foreground">
              No one-off tasks yet — add your first one to build the matrix.
            </p>
          ) : (
            oneOffTasks.map((t: Task) => (
              <div key={t.id} className="flex flex-wrap items-center gap-3 p-4">
                <button
                  onClick={() => hub.startComplete(t)}
                  className="flex size-5 items-center justify-center rounded border border-border text-primary transition-colors hover:bg-accent"
                  aria-label={`Mark ${t.title} done`}
                >
                  <Check className="size-3.5 opacity-0 hover:opacity-100" />
                </button>
                <span className="font-medium">{t.title}</span>
                <PlanChip
                  pillarName={plan.pillarName(t.pillar_id)}
                  goalName={plan.goalName(t.goal_id)}
                />
                <Badge variant="secondary">{t.task_type}</Badge>
                <DueBadge dueDate={t.due_date} />
                <ScheduleBadge task={t} />
                {Number(t.travel_hours) > 0 ? (
                  <Badge variant="outline">
                    <Plane className="size-3" /> {Number(t.travel_hours)}h travel
                  </Badge>
                ) : null}
                {t.handover ? (
                  <Badge variant="outline">
                    <UserRound className="size-3" /> {t.handover}
                  </Badge>
                ) : null}
                <span className="ml-auto text-xs text-muted-foreground">
                  {taskWeeklyTotal(t)}h wk · {Number(t.hours_month)}h mo · {Number(t.hours_quarter)}
                  h qtr
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => hub.openEdit(t)}
                  aria-label="Edit"
                >
                  <Pencil className="size-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => hub.openDuplicate(t)}
                  aria-label="Duplicate"
                >
                  <Copy className="size-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => hub.deleteTask.mutate(t.id)}>
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))
          )}
        </div>
      </CollapsibleSection>
    ),

    done: hub.tasks.some((t: Task) => !isTaskOpen(t) && !isRecurring(t.recurrence)) ? (
      <CollapsibleSection storageKey="done" title="Done for this period" {...moveProps("done")}>
        <div className="panel mt-3 divide-y divide-border">
          {hub.tasks
            .filter((t: Task) => !isTaskOpen(t) && !isRecurring(t.recurrence))
            .map((t: Task) => (
              <div key={t.id} className="flex items-center gap-3 p-3 text-sm">
                <span className="text-muted-foreground line-through">{t.title}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => hub.updateTask.mutate({ id: t.id, patch: { done: false } })}
                >
                  Reopen
                </Button>
                <Button variant="ghost" size="icon" onClick={() => hub.deleteTask.mutate(t.id)}>
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
        </div>
      </CollapsibleSection>
    ) : null,
  };

  const sectionOrder = layout.visibleOrder.filter((key) => Boolean(sections[key]));

  function moveProps(key: string) {
    const index = layout.order.indexOf(key);
    const last = layout.order.length - 1;
    return {
      onMove: (direction: -1 | 1) => layout.move(key, direction),
      canMoveUp: index > 0,
      canMoveDown: index < last,
      onDragStart: () => setDragKey(key),
      onDragOver: (e: ReactDragEvent<HTMLElement>) => {
        if (dragKey && dragKey !== key) e.preventDefault();
      },
      onDrop: () => {
        if (dragKey && dragKey !== key) layout.moveTo(dragKey, key);
        setDragKey(null);
      },
      onDragEnd: () => setDragKey(null),
    };
  }

  return (
    <AppShell
      title="Work hub"
      subtitle="Tasks in, matrix and capacity out."
      action={
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <Link to="/work/timeline">
              Timeline <ArrowRight className="size-4" />
            </Link>
          </Button>
          <Button
            variant="outline"
            onClick={() => hub.openCreate({ is_team_task: true, task_type: "Team delivery" })}
          >
            <Users className="size-4" /> New team task
          </Button>
          <Button onClick={() => hub.openCreate()}>
            <Plus className="size-4" /> New task
          </Button>
        </div>
      }
    >
      <StartHereCard
        tipKey="work"
        title="Run the day, not the tool"
        body="Add a task with its hours, importance and urgency — the matrix, your day and your capacity totals fill themselves in. Regular commitments live on their own Meetings page so they stop crowding the real decisions."
        actions={[{ label: "Regular meetings", to: "/work/regular-meetings" }]}
      >
        <Button variant="outline" size="sm" onClick={() => hub.openCreate()}>
          Add your first task
        </Button>
      </StartHereCard>

      <CapacityPanel
        capacity={capacity}
        totals={totals}
        onCapacityChange={(v) => hub.saveCapacity.mutate(v)}
        breakdown={pillarBreakdown}
        activityBreakdown={activityBreakdown}
        quarterStartMonth={hub.quarterStartMonth}
      />

      <div className="mt-6 space-y-2">
        <LensPresets
          lens={lens}
          lenses={lenses}
          onSelect={selectLens}
          onSave={savePreset}
          onDelete={deletePreset}
          hiddenCount={hiddenByWeek}
        />
        <p className="text-xs text-muted-foreground">{lensSummary(lens)}</p>
      </div>

      {plan.pillars.length ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <span className="label-caps mr-1">Focus</span>
          <FilterChip
            active={filter.kind === "all"}
            onClick={() => setFilter({ kind: "all", id: null })}
          >
            Everything
          </FilterChip>
          {plan.pillars.map((p) => (
            <FilterChip
              key={p.id}
              active={filter.kind === "pillar" && filter.id === p.id}
              onClick={() => setFilter({ kind: "pillar", id: p.id })}
            >
              {p.name}
            </FilterChip>
          ))}
          {plan.goals.map((g) => (
            <FilterChip
              key={g.id}
              active={filter.kind === "goal" && filter.id === g.id}
              onClick={() => setFilter({ kind: "goal", id: g.id })}
            >
              ↳ {g.name}
            </FilterChip>
          ))}
        </div>
      ) : null}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="label-caps mr-1">Ownership</span>
        <FilterChip active={!hub.teamOnly} onClick={() => hub.setTeamOnly(false)}>
          Everything
        </FilterChip>
        <FilterChip active={hub.teamOnly} onClick={() => hub.setTeamOnly(true)}>
          Team tasks only
        </FilterChip>
      </div>

      <TagFilters tasks={allOpenTasks} value={hub.tagFilters} onChange={hub.setTagFilters} />

      {sectionOrder.map((key) => {
        const node = sections[key];
        if (!node) return null;
        return <div key={key}>{node}</div>;
      })}

      <div className="mt-8 space-y-2">
        <p className="label-caps">Sections on this page</p>
        <div className="flex flex-wrap items-center gap-2">
          {layout.order.map((key) => (
            <FilterChip
              key={key}
              active={!layout.hidden.includes(key)}
              onClick={() => layout.toggleHidden(key)}
            >
              {SECTION_LABELS[key] ?? key}
            </FilterChip>
          ))}
          {layout.isCustom ? (
            <Button variant="ghost" size="sm" onClick={layout.reset}>
              <RotateCcw className="size-4" /> Reset section layout
            </Button>
          ) : null}
        </div>
      </div>

      <AllocateDialog task={allocating} onClose={() => setAllocating(null)} />
      <TaskDialog hub={hub} />
      <CompleteDialog hub={hub} />
      <FollowUpDialog hub={hub} />
    </AppShell>
  );
}
